/**
 * Adapter: the REAL high-lift fill campaign → the Tailor 2D/3D/highD surfaces framework
 * (adaptive-sampling primitives) (FXC-9009).
 *
 * The fill campaign is a separate system from the adaptive-sampling mock (highlift/campaigns +
 * /composite/surface). These pure functions map its real outputs onto the shapes TailorSurfaceBuild
 * / Triptych / HeatmapPanel / PointOverlay consume, so the actual fill surfaces build up in the
 * framework we already have — no bespoke re-implementation:
 *   - the composite /surface fused grid  → adaptive-sampling SurfaceResponse (mean/sigma [ny][nx]);
 *   - the campaign's declared box axes    → AxisSpec[] (range / fixed);
 *   - the campaign's landed points        → SamplePoint[] (inputs + CL output);
 *   - a synthetic CampaignState the 3D surface component reads (inputs/outputs/sampler/status).
 */
import type { SurfaceResponse as CompositeSurfaceResponse } from "../../../api/composite";
import type { CampaignAxisSpec, CampaignDetail } from "../../../api/highliftCampaign";
import type { AxisSpec, CampaignState, SamplePoint, SurfaceResponse } from "../../adaptive-sampling/types";
import { FILL_AXES, campaignReplaySteps, type ReplayStep } from "./campaignMath";

const AXIS_META = new Map(FILL_AXES.map((a) => [a.canonical, a]));

/** Linear grid of `n` points across [lo, hi] (n=1 ⇒ the midpoint). */
export function linspace(lo: number, hi: number, n: number): number[] {
  if (n <= 1) return [(lo + hi) / 2];
  return Array.from({ length: n }, (_, i) => lo + ((hi - lo) * i) / (n - 1));
}

/** Reshape a composite flat grid `z[j*nx + i]` (i over x, j over y — the server's row-major order,
 *  matching TrustSurfacePanel) into `grid[y][x]`, mapping null holes to NaN (the framework filters
 *  non-finite cells). Exported for the ExploreBoard's evidence-lens grids (FXC-9009 two-rooms). */
export function reshape(flat: (number | null)[], nx: number, ny: number): number[][] {
  const rows: number[][] = [];
  for (let j = 0; j < ny; j += 1) {
    const row: number[] = [];
    for (let i = 0; i < nx; i += 1) {
      const v = flat[j * nx + i];
      row.push(v == null ? NaN : v);
    }
    rows.push(row);
  }
  return rows;
}

/** Composite /surface response → adaptive-sampling SurfaceResponse. Uses the FUSED confidence-
 *  weighted surface for the estimate (mean) + its sigma; the framework indexes mean[y][x], which
 *  matches the reshape. No oracle truth exists for a real campaign, so truthMean is omitted. */
export function compositeToSurfaceResponse(data: CompositeSurfaceResponse): SurfaceResponse {
  const { nx, ny, x_bounds, y_bounds, fused } = data;
  return {
    x: linspace(x_bounds[0], x_bounds[1], nx),
    y: linspace(y_bounds[0], y_bounds[1], ny),
    mean: reshape(fused.z, nx, ny),
    sigma: reshape(fused.sigma, nx, ny),
  };
}

/** The campaign's declared box → AxisSpec[]: range axes carry [min,max]; held axes are fixed at
 *  their value. Units/label come from FILL_AXES so the framework's slice + pin controls read right. */
export function campaignToAxisSpecs(axes: CampaignAxisSpec[]): AxisSpec[] {
  return axes
    .filter((ax) => AXIS_META.has(ax.axis))
    .map((ax) => {
      const meta = AXIS_META.get(ax.axis)!;
      if (ax.mode === "range" && typeof ax.min === "number" && typeof ax.max === "number") {
        return { name: ax.axis, units: meta.label, mode: "range" as const, min: ax.min, max: ax.max };
      }
      return { name: ax.axis, units: meta.label, mode: "fixed" as const, fixedValue: ax.value ?? 0 };
    });
}

/** The campaign's landed points → SamplePoint[]: inputs are the canonical axes, the CL result is
 *  the output, and cache/failed/pending map onto the framework's status + source so the point
 *  overlay renders each as it lands. */
export function campaignToSamplePoints(steps: ReplayStep[]): SamplePoint[] {
  return steps.map((s, i) => ({
    id: `fill-${i}`,
    iter: s.iterationIndex,
    batchId: `it-${s.iterationIndex}`,
    status: s.failed ? "failed" : "done",
    source: "sampler",
    inputs: {
      alpha_deg: s.alphaDeg,
      flap_deploy_deg: s.deployDeg,
      flaperon_deflection_deg: s.flaperonDeg,
      disk_thrust_loading: s.blowing,
      mach: s.mach,
      re_chord_millions: s.reMillions,
    },
    outputs: s.CL != null ? { CL: s.CL } : undefined,
  }));
}

/** A synthetic CampaignState the 3D surface component reads: it only consumes config.inputs /
 *  config.outputs / config.sampler.engine / status + points. Engine is stamped tailor-mannequin
 *  because TailorSurfaceBuild renders only for the mannequin engines; the surface itself is supplied
 *  via surfaceOverride, so the mock oracle is never touched. Cast to the full type — the unread
 *  config fields (solvers, pools, credits) don't affect the surface render. */
export function campaignToTailorState(campaign: CampaignDetail): CampaignState {
  const axes = campaignToAxisSpecs(campaign.config?.axes ?? []);
  const running = campaign.state?.status === "running" || campaign.state?.status === "paused";
  return {
    id: campaign.campaign_id,
    status: running ? "running" : "done",
    iteration: campaign.state?.iterations?.length ?? 0,
    points: campaignToSamplePoints(campaignReplaySteps(campaign)),
    config: {
      name: campaign.config?.name ?? "fill",
      inputs: axes,
      outputs: [{ name: "CL", weight: 1 }],
      sampler: { engine: "tailor-mannequin", label: "Tailor fill" },
    },
  } as unknown as CampaignState;
}
